import { createSlice } from "@reduxjs/toolkit";
import * as actions from './api';
import axios from 'axios';

export const courseSlice = createSlice({
    name: 'course',
    initialState: {
        loading: false,
        error: null,
        list: [],
        form: null,
        popup: false,
    },
    reducers: {
        coursesRequested: (state, action) => {
            state.loading = true;
        },
        coursesReceived: (state, action) => {
            state.loading = false;
            const { data, programId } = action.payload;
            const newCourses = data.map(course => ({ ...course, programId }));
            
            const otherCourses = state.list.filter(c => c.programId !== programId);
            state.list = [...otherCourses, ...newCourses];
        },
        coursesError: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        courseRequested: (state, action) => {
            state.loading = true;
        },
        courseCreated: (state, action) => {
            state.loading = false;
            state.popup = false;
            // Ensure the course has programId field for consistent filtering
            const newCourse = { 
                ...action.payload, 
                programId: action.payload.program_id || action.payload.programId 
            };
            state.list = [newCourse, ...state.list];
            state.form = null;
        },
        courseUpdated: (state, action) => {
            state.loading = false;
            state.popup = false;
            state.form = null;
            const index = state.list.findIndex(course => course.id === action.payload.id);
            // Ensure the course has programId field for consistent filtering
            const updatedCourse = { 
                ...action.payload, 
                programId: action.payload.program_id || action.payload.programId 
            };
            state.list[index] = updatedCourse;
        },
        courseDeleted: (state, action) => {
            state.loading = false;
            state.list = state.list.filter(course => course.id !== action.payload.id);
        },
        courseError: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        showCoursePopup: (state, action) => {
            state.popup = true;
            state.form = action.payload;
        },
        closePopup: (state, action) => {
            state.popup = false;
            state.form = null;
        }
    }
});

const {
    coursesRequested,
    coursesReceived,
    coursesError,
    courseRequested,
    courseError,
    courseCreated,
    courseUpdated,
    courseDeleted,
    showCoursePopup,
    closePopup,
} = courseSlice.actions;

const getCourses = (programId) => async (dispatch, getState) => {
    dispatch(coursesRequested());
    try {
        // Use the correct API endpoint from OpenAPI spec
        const url = programId ? `/app/programs/${programId}/courses` : '/app/courses';
        const response = await axios.request({
            baseURL: `${process.env.REACT_APP_BASE_URL}/api/v1`,
            url: url,
            method: 'GET',
            headers: {
                'X-Api-Key': process.env.REACT_APP_APP_TOKEN
            }
        });
        dispatch(coursesReceived({ data: response.data.data, programId }));
    } catch (error) {
        dispatch(coursesError({ error: error.message }));
    }
}

const createCourse = (image, data) => (dispatch, getState) => {
    const accessToken = getState().auth.accessToken;

    dispatch(
        actions.uploadCallBegan(
            {
                url: '/file/upload',
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + accessToken
                },
                data: image,
                onStart: courseRequested.type,
                onSuccess: (imageResponse) => actions.apiCallBegan(
                    {
                        url: '/courses',
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + accessToken
                        },
                        data: {
                            ...data,
                            imageId: imageResponse.id
                        },
                        onStart: courseRequested.type,
                        onSuccess: courseCreated.type,
                        onError: courseError.type
                    }
                ),
                onError: courseError.type
            }
        )
    );
}

const updateCourse = (image, data) => (dispatch, getState) => {
    const accessToken = getState().auth.accessToken;

    if (!image) {
        dispatch(
            actions.apiCallBegan(
                {
                    url: '/courses/' + data.id,
                    method: 'PUT',
                    headers: {
                        'Authorization': 'Bearer ' + accessToken
                    },
                    data: data,
                    onStart: courseRequested.type,
                    onSuccess: courseUpdated.type,
                    onError: courseError.type
                }
            )
        );
        return;
    }

    dispatch(
        actions.uploadCallBegan(
            {
                url: '/file/upload',
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + accessToken
                },
                data: image,
                onStart: courseRequested.type,
                onSuccess: (imageResponse) => actions.apiCallBegan(
                    {
                        url: '/courses/' + data.id,
                        method: 'PUT',
                        headers: {
                            'Authorization': 'Bearer ' + accessToken
                        },
                        data: {
                            ...data,
                            imageId: imageResponse.id
                        },
                        onStart: courseRequested.type,
                        onSuccess: courseUpdated.type,
                        onError: courseError.type
                    }
                ),
                onError: courseError.type
            }
        )
    );
}

const deleteCourse = (data) => (dispatch, getState) => {
    const accessToken = getState().auth.accessToken;

    dispatch(
        actions.apiCallBegan(
            {
                url: '/courses/' + data.id,
                method: 'DELETE',
                headers: {
                    'Authorization': 'Bearer ' + accessToken
                },
                onStart: courseRequested.type,
                onSuccess: courseDeleted.type,
                onError: courseError.type
            }
        )
    );
}

export {
    getCourses,
    createCourse,
    updateCourse,
    deleteCourse,
    showCoursePopup,
    closePopup
};

export default courseSlice.reducer;